<template>
  <div class="base-icon-picker" :class="rootClasses">
    <div class="base-icon-picker__row">
      <BaseScrollHorizontal class="base-icon-picker__scroll">
        <div class="base-icon-picker__icons">
          <button
            v-for="(iconName, index) in stripList"
            :key="`${iconName}-${index}`"
            type="button"
            class="base-icon-picker__icon-btn"
            :class="{ 'base-icon-picker__icon-btn--selected': modelValue === iconName }"
            :tabindex="props.tabindex"
            :aria-label="iconName"
            @click="handleSelect(iconName)"
          >
            <BaseIcon
              :name="iconName"
              :size="iconGlyphSize"
              color="var(--color-foreground-regular)"
            />
          </button>
        </div>
      </BaseScrollHorizontal>
      <div v-if="dropdownList.length > 0" class="base-icon-picker__more">
        <BaseDropdownWrapper
          placement="bottom-end"
          max-height="min(360px, 65vh)"
          width="fill"
        >
          <template #control="{ isOpen: isDropdownOpen }">
            <button
              type="button"
              class="base-icon-picker__more-btn"
              :tabindex="props.tabindex"
              aria-haspopup="listbox"
              :aria-expanded="isDropdownOpen"
            >
              <span
                class="base-icon-picker__more-arrow"
                :class="{ 'base-icon-picker__more-arrow--open': isDropdownOpen }"
              >
                <BaseIcon
                  name="vuesax:arrow-down"
                  :size="moreArrowIconSize"
                  color="var(--color-foreground-regular)"
                />
              </span>
            </button>
          </template>
          <template #dropdown>
            <div class="base-icon-picker__palette" role="listbox">
              <button
                v-for="(iconName, index) in dropdownList"
                :key="`all-${iconName}-${index}`"
                type="button"
                class="base-icon-picker__palette-item"
                :class="{ 'base-icon-picker__icon-btn--selected': modelValue === iconName }"
                role="option"
                :aria-selected="modelValue === iconName"
                @click="handleSelect(iconName)"
              >
                <BaseIcon
                  :name="iconName"
                  :size="iconGlyphSize"
                  color="var(--color-foreground-regular)"
                />
              </button>
            </div>
          </template>
        </BaseDropdownWrapper>
      </div>
    </div>
    <BaseTypography
      v-if="props.errorMessage"
      class="base-icon-picker__error"
      type="text-mini-alt"
      color="var(--color-foreground-danger)"
    >
      {{ props.errorMessage }}
    </BaseTypography>
  </div>
</template>

<script setup lang="ts">
import { VUESAX_ICONS_DICTIONARY } from '~/shared/const/vuesaxIconsDictionary'
import BaseDropdownWrapper from '../dropdown-wrapper/index.vue'
import BaseIcon from '../icon/index.vue'
import BaseScrollHorizontal from '../scroll-horizontal/index.vue'
import BaseTypography from '../typography/index.vue'

interface Props {
  name?: string
  tabindex?: number
  variant?: 'light' | 'secondary'
  /** true — круглые кнопки и подложка-капсула (полукруглые торцы); false — rem(8) у кнопок и подложки */
  rounded?: boolean
  /** Сколько иконок в горизонтальной полоске; в дропдауне — полный список `icons` */
  stripCount?: number
  icons?: readonly string[]
  errorMessage?: string | boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light',
  rounded: false,
  stripCount: 10,
  icons: () => [...VUESAX_ICONS_DICTIONARY],
})

const modelValue = defineModel<string>({ required: true })

const stripList = computed(() => {
  const n = Math.max(0, Math.floor(props.stripCount))
  return [...props.icons].slice(0, n)
})

const dropdownList = computed(() => [...props.icons])

const rootClasses = computed(() => ({
  [`base-icon-picker--variant-${props.variant}`]: true,
  'base-icon-picker--rounded': props.rounded,
}))

const iconGlyphSize = computed(() => (props.rounded ? '18px' : '22px'))
const moreArrowIconSize = computed(() => (props.rounded ? '18px' : '20px'))

function handleSelect(iconName: string) {
  modelValue.value = iconName
}
</script>

<style lang="scss" scoped>
.base-icon-picker {
  width: 100%;

  &__row {
    display: flex;
    align-items: center;
    gap: rem(8px);
    width: 100%;
    border-radius: rem(8px);
    padding: rem(8px);
  }

  &__scroll {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__icons {
    display: flex;
    align-items: center;
    gap: rem(8px);
    padding: rem(4px) 0;
    width: max-content;
  }

  &__icon-btn {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: rem(40px);
    height: rem(40px);
    border-radius: rem(8px);
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;
    background-color: var(--color-surface-placeholder-600);

    &--selected {
      border-color: var(--color-foreground-primary);
    }
  }

  &__more {
    flex-shrink: 0;
  }

  &__more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: rem(40px);
    height: rem(40px);
    border-radius: rem(8px);
    border: none;
    cursor: pointer;
    background-color: var(--color-surface-placeholder);
  }

  &__more-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__palette {
    display: grid;
    grid-template-columns: repeat(5, rem(40px));
    gap: rem(8px);
    padding: rem(4px);
    justify-content: center;
  }

  &__palette-item {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: rem(40px);
    height: rem(40px);
    border-radius: rem(8px);
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;
    background-color: var(--color-surface-placeholder-600);

    &.base-icon-picker__icon-btn--selected {
      border-color: var(--color-foreground-primary);
    }
  }

  &__error {
    margin-top: rem(6px);
  }

  &--variant-light .base-icon-picker__row {
    background-color: var(--color-surface-bg);
  }

  &--variant-secondary .base-icon-picker__row {
    background-color: var(--color-surface-placeholder);
  }

  &--variant-secondary .base-icon-picker__more-btn {
    background-color: var(--color-surface-bg);
  }

  &--rounded .base-icon-picker__row {
    height: rem(44px);
    min-height: rem(44px);
    padding: rem(8px);
    border-radius: 9999px;
    box-sizing: border-box;
  }

  &--rounded .base-icon-picker__scroll {
    display: flex;
    align-items: center;
    min-height: 0;
  }

  &--rounded .base-icon-picker__icons {
    padding: 0;
    align-items: center;
  }

  &--rounded .base-icon-picker__icon-btn,
  &--rounded .base-icon-picker__palette-item,
  &--rounded .base-icon-picker__more-btn {
    box-sizing: border-box;
    width: rem(28px);
    height: rem(28px);
    border-radius: 50%;
  }

  &--rounded .base-icon-picker__palette {
    grid-template-columns: repeat(5, rem(28px));
  }
}
</style>
